import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Brand } from '../../../../core/models/brand';
import { DashboardReportingQueryTypes, IReportDateRange, ReportDao } from '../../../../core/services/dao/reportDao';
import * as moment from 'moment-timezone';
import { DemoConstants } from '../../../../assets/data/demoConstants';
import { ConstantMan } from '../../../../core/services/constantMan';

type CreativeMappingValue = {
  affiliate: string;
  baselineCount: number;
  eventTimestamp: string;
  dmaName: string;
  isci: string;
  lift: number;
  pvCount: number;
  sessionCount: number;
  baselineSessionsPerSecond?: string;
  baselineSessionCount?: number;
  eventLength?: number;
  eventId?: string;
  creativeLength?: number;
};

type QueryResRecord = CreativeMappingValue;

type CreativeMapping = {
  [key: string]: CreativeMappingValue[];
};

@Component({
  selector: 'micromoment-timeline-tile',
  templateUrl: './micromoment-timeline-tile.component.html',
  styleUrls: ['micromoment-timeline-tile.component.scss']
})
export class MicromomentTimelineTileComponent implements OnInit, OnDestroy {
  dateRangeSubscription: Subscription;
  @Input() brand: Brand;
  // @Input() timezone: string;
  timezone = 'America/Chicago';
  @Input() creatives: Array<string>;

  public currentDateRange: IReportDateRange;
  private subs: any[] = [];
  public loadingData = true;
  public chartType: any = 'Line';
  public chartData: any = { series: [], labels: [] };
  // public chartOpts: LineChartOptions = {};
  // public chartEvents: ChartEvent = {};
  public displayField = 'lift';
  public allRecords: any;
  public selectedCreative = 'All';
  private creativeMapping: { [key: string]: CreativeMappingValue[] };
  private maxSpots = 20;
  public overSingleDayMax = false;
  private chartColors: string[];
  private xValues = new Set();
  private allPoints = [];
  private seriesOpMapping = {};
  private needsOffset = [];
  private pointOffsetOpts: any = {};
  private singleCreativeSelected: boolean = false;
  private listener;

  public timescale = 'day';
  public initialTimescale = 'day';
  @ViewChild('mmTimelineChart', { static: false }) mmChart: any;

  public _resetZoom;

  constructor(private reportDao: ReportDao) {
    this.chartColors = ConstantMan.CHARTIST_COLORS;
    this.allRecords = DemoConstants.mMRecords as unknown as QueryResRecord[]
  }

  ngOnInit() {
    this.currentDateRange = this.reportDao.getReportDateRangeVal();
    let sub = (this.dateRangeSubscription = this.reportDao.getReportDateRangeObs().subscribe((range) => {
      this.currentDateRange = range;
      this.fetchTileData();
    }));
    this.subs.push(sub);
    this.fetchTileData();
  }

  fetchTileData() {

    this.loadingData = false;
    this.timescale = this.getTimescale(
      moment.tz(this.currentDateRange.start, 'UTC').valueOf(),
      moment.tz(this.currentDateRange.end, 'UTC').valueOf()
    );
    this.initialTimescale = this.timescale;
    this.allRecords = DemoConstants.mMTimeLine
    this.initChartData();
  }

  initCreativeMapping() {
    let creativeMapping = {};
    for (let record of this.allRecords) {
      this.updateCreativeMapping(record, creativeMapping);
    }
    this.creativeMapping = creativeMapping;
  }

  initChartData() {
    this.initCreativeMapping();
    this.creatives = ['All'].concat(Object.keys(this.creativeMapping));
    this.setChartData(this.creativeMapping);
  }

  refreshChartOnCreativeChange() {
    this.singleCreativeSelected = this.selectedCreative === 'All';
    let toMap;
    let newCreativeMapping = {};
    toMap = this.creativeMapping;
    if (this.selectedCreative !== 'All') {
      for (let key of Object.keys(toMap)) {
        if (key === this.selectedCreative) {
          for (let record of toMap[key]) {
            this.updateCreativeMapping(record, newCreativeMapping);
          }
        }
      }
    } else {
      let allAirings = [];
      Object.values(toMap).map((arr) => (allAirings = allAirings.concat(arr)));
      newCreativeMapping = toMap;
    }
    this.setChartData(newCreativeMapping);
  }

  private updateCreativeMapping(record: QueryResRecord, map: CreativeMapping): void {
    let isci = record.isci;
    let dma = record.dmaName;
    let affilate = record.affiliate;
    if (!map[isci]) {
      map[isci] = [
        {
          sessionCount: record.sessionCount,
          isci: isci,
          pvCount: record.pvCount,
          dmaName: dma,
          lift: record.lift,
          eventTimestamp: record.eventTimestamp,
          affiliate: affilate,
          baselineSessionCount: record.baselineSessionCount
        } as CreativeMappingValue
      ];
    } else {
      let tmp = map[isci];
      tmp.push({
        sessionCount: record.sessionCount,
        isci: isci,
        pvCount: record.pvCount,
        dmaName: dma,
        lift: record.lift,
        eventTimestamp: record.eventTimestamp,
        affiliate: affilate,
        baselineSessionCount: record.baselineSessionCount
      } as CreativeMappingValue);
      map[isci] = tmp;
    }
  }

  formatTime(date: string) {
    return moment.tz(date, 'YYYY-MM-DD HH:mm:ss.SSS', 'UTC').tz(this.timezone).format('llll');
  }

  formatDmaText(record) {
    let hasDMA = !!record.dmaName;
    let dmaName = hasDMA ? record.dmaName.split(',')[0].split('(')[0] : null;
    return dmaName ? dmaName : 'National';
  }

  formatLiftFromRecord(record) {
    return parseInt((record.lift * 100).toFixed(0));
  }

  changeDisplayValue(evt) {
    this.displayField = evt.value;
    this.refreshChartOnCreativeChange();
  }

  changeSelectedCreative(evt) {
    this.selectedCreative = evt.value;
    this.refreshChartOnCreativeChange();
  }

  setChartData(creativeMapping: any): any {
    let high;
    if(this.displayField === 'lift'){
      high = 1050
    }else{
      high = 11
    }
    this.chartData.labels = [];
    let series = [];
    let maxY;
    let mapping = this.getTopNCreativesFromCreativeMapping(creativeMapping);
    const generated = this.generateDefaultSeries(mapping);
    series = generated.series;
    maxY = generated.maxY;
    this.chartData.series = series;

    let legendContainer = document.getElementById('micromoment-legend');
    let currentLegend = document.getElementsByClassName('micromoment-legend-el').item(0);
    if (currentLegend) currentLegend.remove();
    // this.chartOpts = {
    //   showLine: false,
    //   fullWidth: true,
    //   scaleShowValues: true,
    //   axisX: {
    //     high: 1652575718384,
    //     type: Chartist.AutoScaleAxis,
    //     onlyInteger: true,
    //     scaleMinSpace: 80,
    //     position: 'end',
    //     labelInterpolationFnc: (value) => {
    //       switch (this.timescale) {
    //         case 'month':
    //           return moment(value).tz(this.timezone).format('MMM D, YYYY');
    //         case 'day':
    //           return moment(value).tz(this.timezone).format('dddd - MM/DD/YYYY');
    //       }
    //     }
    //   },
    //   axisY: {
    //     high: high,
    //     type: Chartist.AutoScaleAxis
    //   },
    //   showPoint: true,
    //   chartPadding: { top: 25, right: 75, left: 10, bottom: 0 },
    //   plugins: [
    //
    //   ]
    // } as LineChartOptions;
    this.xValues = new Set<any>();
    this.allPoints = [];
    // this.chartEvents = {
    //   draw: (data: any) => {
    //     if (data.type === 'point') {
    //       const idx = Object.keys(this.creativeMapping).indexOf(data.series.name);
    //       if (idx !== -1) {
    //         data.element._node.style.stroke = this.chartColors[idx];
    //       }
    //       data.element._node.style.strokeWidth = '18px';
    //
    //       let x = data.element._node.x1.baseVal.value;
    //       let y = data.element._node.y1.baseVal.value;
    //       this.xValues.add(x);
    //       let strokeWidth = data.element._node.style.strokeWidth;
    //       this.allPoints.push({ x, y, strokeWidth });
    //     }
    //   },
    //   created: (data: any) => {
    //     /// find overlapping points and
    //     if (this.allPoints.length > 0) {
    //       this.allPoints.sort((a, b) => a.x - b.x);
    //       let maxX = this.allPoints[this.allPoints.length - 1].x;
    //       Array.from(this.xValues.values()).forEach((x: number) => {
    //         this.seriesOpMapping[x] = true;
    //         let filtered = [...this.allPoints].filter((p) => p.x === x).sort((a, b) => b.y - a.y);
    //         for (let i = 1; i < filtered.length; i++) {
    //           if (Math.abs(filtered[i].y - filtered[i - 1].y) < this.pointOffsetOpts.maxYDiff) {
    //             this.needsOffset.push(filtered[i]);
    //           }
    //         }
    //       });
    //
    //       data.svg._node.childNodes[1].childNodes.forEach((child) => {
    //         child.childNodes.forEach((_point) => {
    //           this.applyPointOffSet(_point, this.needsOffset, this.seriesOpMapping, maxX).then(() => {});
    //         });
    //       });
    //     }
    //   }
    // };
  }

  getTimescale(min, max) {
    let diff = max - min;
    if (diff > 1000 * 60 * 60 * 24 * 60) {
      //more than 60 days
      return 'month';
    } else if (diff > 1000 * 60 * 60 * 24 * 4) {
      //more than 4 days
      return 'day';
    } else if (diff > 1000 * 60 * 60 * 4) {
      //more than 4 hours
      return 'hour';
    } else {
      return 'minute';
    }
  }

  private applyPointOffSet(_point, needsOffset, seriesOpMapping, maxX): Promise<any> {
    return new Promise((resolve) => {
      let x = _point.x1.baseVal.value;
      let y = _point.y1.baseVal.value;
      let strokeWidth = _point.style.strokeWidth;
      let width = parseInt(strokeWidth.split('px')[0]);
      if (this.pointOffsetOpts.shouldOffset) {
        let newX = x === maxX ? x + 45 : x + 100;
        _point.setAttribute('x1', newX);
        _point.setAttribute('x2', newX);
      }

      let foundPoint = needsOffset.find((p) => p.x === x && p.y === y && p.strokeWidth === strokeWidth);

      if (foundPoint) {
        x = _point.x1.baseVal.value;
        y = _point.y1.baseVal.value;
        let newX = seriesOpMapping[x] ? x + (width + 5) : x - (width + 5);
        seriesOpMapping[x] = !seriesOpMapping[x];
        if (!isNaN(newX)) {
          _point.setAttribute('x1', newX);
          _point.setAttribute('x2', newX);
        } else {
        }
      }
      resolve(true);
    });
  }

  generateDefaultSeries(mapping: CreativeMapping): { series: any[]; maxY: number; maxX: number } {
    let series = [];
    let maxY = Number.NEGATIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    for (let key of Object.keys(mapping)) {
      let tmp = {
        name: key,
        data: mapping[key].map((record) => {
          let val;
          switch (this.displayField) {
            case 'lift':
              val = this.formatLiftFromRecord(record);
              break;
            case 'sessions':
              val = record.sessionCount;
              break;
          }
          let isci = record.isci;
          let affiliate = record.affiliate;
          let labelStyles = 'display: block; color:#4A762F; text-align: start; font-weight: bold';
          maxY = Math.max(maxY, val, 0);
          maxX = Math.max(maxX, moment.tz(record.eventTimestamp, 'YYYY-MM-DD HH:mm:ss.SSS', 'UTC').valueOf());
          return {
            x: moment.tz(record.eventTimestamp, 'YYYY-MM-DD HH:mm:ss.SSS', 'UTC').valueOf(),
            y: Math.max(0, val),
            meta: `
                <div style="text-align: start; margin-bottom: 5px">
                      <span style="${labelStyles}">
                          Creative
                      </span> 
                      ${isci}
                  </div>
                  <div style="text-align: start; margin-bottom: 5px">
                      <span style="${labelStyles}">
                          Airing Details
                      </span> 
                      ${affiliate} - ${this.formatDmaText(record)} - ${this.formatTime(record.eventTimestamp)}
                  </div>
                  <span style="${labelStyles}">
                      Lift Details
                  </span>
                  <div style="text-align: start">
                      ${this.formatLiftFromRecord(record)}% lift in Micro-moment
                  </div>
                  <div style="text-align: start">
                      5 minutes before: ${record.baselineSessionCount} sessions
                  </div>
                  <div style="text-align: start">
                      5 minutes during: ${record.sessionCount} sessions
                  </div>
            `
          };
        })
      };
      series.push(tmp);
    }
    return { series, maxY, maxX };
  }
  randomIntFromInterval(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  getTopNCreativesFromCreativeMapping(creativeMapping: any): { [key: string]: CreativeMappingValue[] } {
    let countMapping = {};
    for (let spotKey of Object.keys(creativeMapping)) {
      let total = 0;
      for (let affiliateKey of Object.keys(creativeMapping[spotKey])) {
        total += creativeMapping[spotKey][affiliateKey].sessionCount;
      }
      countMapping[spotKey] = total;
    }
    let sortedCounts = (Object.values(countMapping) as number[])
      .sort((a, b) => {
        return b - a;
      })
      .slice(0, this.maxSpots);
    let topSpots = {};
    for (const [key, value] of Object.entries(countMapping)) {
      if (sortedCounts.includes(value as number) && Object.keys(topSpots).length < this.maxSpots) {
        topSpots[key] = creativeMapping[key];
      }
    }
    return topSpots;
  }

  resetZoom() {
    if (this._resetZoom) {
      this.timescale = this.initialTimescale;
      this._resetZoom();
      this._resetZoom = null;
    }
  }

  chartInitialized(chart) {
    this.listener = window.matchMedia('print').addEventListener('change', () => {
      chart.update();
    });
  }

  ngOnDestroy() {
    if (this.listener) {
      window.matchMedia('print').removeEventListener('change', this.listener);
    }
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
